import styled from "styled-components";

import {
  DATA_E2E_KEY,
  IStyledAttributes,
  STATION_PIN_HEIGHT_PX,
  STATION_PIN_PATH,
  STATION_PIN_TIP_X,
  STATION_PIN_TIP_Y,
  STATION_PIN_WIDTH_PX,
} from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import { MAPLIBRE_STATION_PIN_BADGE_TOP_OVERSHOOT_PX } from "./maplibre-station-pin.constants";

/**
 * 0×0 tip anchor: MapLibre `anchor="center"` pins the path tip to lat/lng.
 * Badge/finish overflow cannot shift the marker box.
 */
export const MapLibreStationPinRoot = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "maplibre-station-pin",
})`
  position: relative;
  width: 0;
  height: 0;
  overflow: visible;
  pointer-events: auto;
  line-height: 0;

  & .maplibre-station-pin-body {
    position: absolute;
    left: -${STATION_PIN_TIP_X}px;
    top: -${STATION_PIN_TIP_Y}px;
    width: ${STATION_PIN_WIDTH_PX}px;
    height: ${STATION_PIN_HEIGHT_PX}px;
    box-sizing: border-box;
    overflow: visible;
  }

  & .maplibre-station-pin-shape {
    display: block;
    width: ${STATION_PIN_WIDTH_PX}px;
    height: ${STATION_PIN_HEIGHT_PX}px;
    overflow: visible;
  }

  /* Leaflet twin: .map-marker-icon-bg-ava — FinishSVG fills the clipped pin. */
  & .maplibre-station-pin-finish {
    position: absolute;
    inset: 0;
    clip-path: path("${STATION_PIN_PATH}");
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    overflow: hidden;

    & > svg {
      display: block;
      width: 27px;
      height: 37px;
      flex-shrink: 0;
    }
  }

  /*
   * Leaflet twin: .map-marker-icon-ava — which sets no size, so each glyph draws at its own. Forcing a
   * square here distorted them: the home is natively 20x17 and the flag 7x11, so an 18x18 box stretched the
   * flag to two and a half times its width and threw both off the pin head's centre.
   */
  & .maplibre-station-pin-ava {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -80%);
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;

    & > svg {
      display: block;
    }
  }

  & .maplibre-station-pin-badge {
    position: absolute;
    /* Overhangs the pin's top edge, so it — not the pin — is what a popup has to clear. */
    top: -${MAPLIBRE_STATION_PIN_BADGE_TOP_OVERSHOOT_PX}px;
    inset-inline-end: -10px;
    box-sizing: border-box;
    min-width: 22px;
    height: 22px;
    padding: 0 5px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    color: ${rydeColors.white};
    line-height: 1;
    pointer-events: none;
  }
`;

export const MapLibreStationPinBadge = styled.span.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "maplibre-station-pin-badge",
  className: "maplibre-station-pin-badge",
})<{ $backgroundColor: string }>`
  background-color: ${({ $backgroundColor }) => $backgroundColor};
`;

export const MapLibreStationPinFinish = styled.span.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "maplibre-station-pin-finish",
  className: "maplibre-station-pin-finish",
})<{ $color: string }>`
  color: ${({ $color }) => $color};
`;

export const MapLibreStationPinAva = styled.span.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "maplibre-station-pin-ava",
  className: "maplibre-station-pin-ava",
})<{ $color: string }>`
  color: ${({ $color }) => $color};
`;
